'use client'

/**
 * Écran d'erreur commun (boundaries Next) — design BLC Médias.
 * Purement visuel : les actions (reset, nettoyage…) sont fournies par l'appelant.
 */
import type { ReactNode } from 'react'
import { AlertTriangle, RotateCcw, Home } from 'lucide-react'
import { GlowBackdrop } from './AuthUI'

export function ErrorScreen({ title, text, error, onRetry, homeHref = '/', extra, fullScreen = true }: {
  title: string
  text?: ReactNode
  error?: Error & { digest?: string }
  onRetry?: () => void
  homeHref?: string
  extra?: ReactNode
  fullScreen?: boolean
}) {
  const hasDetails = !!(error && (error.message || error.digest || error.stack))
  return (
    <div className={fullScreen
      ? 'relative min-h-screen bg-sbg text-white flex items-center justify-center px-4 py-10 overflow-hidden'
      : 'relative px-4 sm:px-5 md:px-7 pt-6 pb-10 max-w-3xl mx-auto text-white'}>
      {fullScreen && <GlowBackdrop />}
      <div className="relative w-full max-w-2xl">
        <div className="text-[11px] font-semibold tracking-[0.28em] text-white/55 mb-3">BLC MÉDIAS</div>
        <div className="flex items-start gap-4">
          <span className="w-12 h-12 shrink-0 rounded-2xl grid place-items-center bg-rose-500/15 text-rose-400 border border-rose-400/20">
            <AlertTriangle size={22} />
          </span>
          <div className="min-w-0">
            <h1 className="text-[24px] md:text-[32px] font-extrabold tracking-tight leading-tight">{title}</h1>
            {text && <p className="text-sm md:text-[15px] text-white/60 mt-2">{text}</p>}
          </div>
        </div>

        {hasDetails && (
          <details open className="mt-6 rounded-2xl bg-spanel/90 border border-white/[0.07] overflow-hidden group">
            <summary className="cursor-pointer select-none px-4 py-3 text-[13px] font-semibold text-white/75 hover:text-white">Détails techniques</summary>
            <div className="px-4 pb-4 text-[12px] font-mono whitespace-pre-wrap break-words max-h-[50vh] overflow-auto text-white/75 border-t border-white/[0.06] pt-3">
              <strong className="text-a5">Message :</strong>{'\n'}{error!.message || '(aucun)'}
              {error!.digest && <>{'\n\n'}<strong className="text-a5">Digest :</strong>{'\n'}{error!.digest}</>}
              {error!.stack && <>{'\n\n'}<strong className="text-a5">Stack :</strong>{'\n'}{error!.stack}</>}
            </div>
          </details>
        )}

        <div className="flex flex-wrap gap-2.5 mt-6">
          {onRetry && (
            <button type="button" onClick={onRetry}
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-a1 to-a2 shadow-[0_0_22px_rgb(var(--a2)/.45)] hover:brightness-110 active:scale-[0.97] transition">
              <RotateCcw size={16} /> Réessayer
            </button>
          )}
          <a href={homeHref}
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white/90 bg-white/[0.07] border border-white/10 hover:bg-white/[0.12] transition">
            <Home size={16} /> Accueil
          </a>
          {extra}
        </div>
      </div>
    </div>
  )
}
